import { useCallback, useMemo } from 'react';
import { useLoading } from '@toss/use-loading';
import { useSetAtom } from 'jotai';
import { useTranslation } from 'next-i18next';
import { isRateLimitedError } from '@/api/apiUtil';
import { useUploadImage } from '@/api/quiz';
import { useValidateImage } from '@/hooks';
import { compressImageAndChangeToWebp } from '@/shared/utils/compressImageAndChangeToWebp';
import { alertDialog } from '@/ui_v3';
import { IMAGE_UPLOAD_CONSTANTS } from '../constant';
import { pendingImageUploadIdsAtom } from '../stores/imageUpload.atoms';

/**
 * 이미지 업로드 비동기 동작 및 업로드 상태를 관리하는 hook
 */
export const useImageUploadInput = ({
  onUploadComplete,
  pendingId
}: {
  onUploadComplete: (imageUrl: string) => void;
  pendingId?: string;
}) => {
  const [isUploading, startUploading] = useLoading();
  const {
    t
  } = useTranslation(['build', 'common']);
  const validate = useValidateImage();
  const setPendingIds = useSetAtom(pendingImageUploadIdsAtom);
  const {
    ALLOWED_IMAGE_EXTENSIONS,
    MAX_IMAGE_DIMENSION,
    MAX_MB_SIZE,
    MAX_IMAGE_COUNT
  } = IMAGE_UPLOAD_CONSTANTS;
  const {
    mutateAsync: uploadImageToServer
  } = useUploadImage();
  const uploadImage = useCallback(async (file: File) => {
    try {
      const isImageValid = await validate({
        file,
        validation: {
          mbSize: MAX_MB_SIZE,
          maxDimension: MAX_IMAGE_DIMENSION,
          extensions: ALLOWED_IMAGE_EXTENSIONS,
          countLimit: MAX_IMAGE_COUNT
        }
      });
      if (!isImageValid) {
        return null;
      }
      const webpFile = await compressImageAndChangeToWebp(file);

      // 서버에 이미지 업로드
      const result = await startUploading(uploadImageToServer({
        file: webpFile
      }));
      return result.data.imageUrl;
    } catch (error) {
      console.warn(error);
      alertDialog.open({
        header: {
          title: t('build.imageUploadFail.title')
        },
        body: isRateLimitedError(error) ? t('build.actionError.waitTry') : t('build.imageUploadFail.content'),
        confirmText: t('common:common.alertDialog.confirmText')
      });
      return null;
    }
  }, [validate, startUploading, uploadImageToServer, t, ALLOWED_IMAGE_EXTENSIONS, MAX_IMAGE_DIMENSION, MAX_MB_SIZE, MAX_IMAGE_COUNT]);
  const handleFileChange = useCallback(async (file: File | null) => {
    if (file) {
      if (pendingId) setPendingIds(prev => [...prev, pendingId]);
      try {
        const imageUrl = await uploadImage(file);
        if (!imageUrl) return;
        onUploadComplete(imageUrl);
      } catch (_) {
        // FIXME: error시 file input clear 필요
        console.warn(_);
      } finally {
        if (pendingId) {
          setPendingIds(prev => {
            const index = prev.indexOf(pendingId);
            return prev.filter((_, i) => i !== index);
          });
        }
      }
    }
  }, [uploadImage, onUploadComplete, pendingId, setPendingIds]);
  const accept = useMemo(() => ALLOWED_IMAGE_EXTENSIONS.map(extension => `image/${extension}`).join(', '), [ALLOWED_IMAGE_EXTENSIONS]);
  return {
    accept,
    isUploading,
    handleFileChange
  };
};